import React from 'react';
import {
  View,
  StyleSheet,
  TouchableOpacity,
  Dimensions,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { Text } from '@/components/common/text';
import type { ToastConfigParams } from 'react-native-toast-message';
import { SVG_COMPONENTS } from '@/assets/admin';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';

export type ToastType = 'success' | 'error' | 'warning' | 'info' | 'loading';

const { width: screenWidth } = Dimensions.get('window');

interface ToastSwatch {
  backgroundColor: string;
  borderColor: string;
}

const useToastSwatches = (): Record<ToastType, ToastSwatch> =>
  useThemedStyles(({ semantic }) => ({
    success: { backgroundColor: semantic.status.success, borderColor: semantic.status.success },
    error: { backgroundColor: semantic.status.error, borderColor: semantic.status.error },
    warning: { backgroundColor: semantic.status.warning, borderColor: semantic.status.warning },
    info: { backgroundColor: semantic.status.info, borderColor: semantic.status.info },
    loading: { backgroundColor: semantic.status.info, borderColor: semantic.status.info },
  }));

const CustomToast = ({ text1, hide, type }: ToastConfigParams<any> & { type: ToastType }) => {
  const swatches = useToastSwatches();
  const colors = swatches[type] || swatches.info;

  return (
    <View style={styles.toastWrapper}>
      <View
        style={[
          styles.toast,
          {
            backgroundColor: colors.backgroundColor,
            borderColor: colors.borderColor,
          },
        ]}
      >
        <TouchableOpacity style={styles.toastContent} onPress={() => hide()} activeOpacity={0.8}>
          {type === 'loading' && (
            <View style={styles.iconContainer}>
              <ActivityIndicator size="small" color="#FFFFFF" />
            </View>
          )}

          <Text style={styles.message} numberOfLines={5} scale="content">
            {text1}
          </Text>

          {type !== 'loading' && (
            <TouchableOpacity style={styles.closeButton} onPress={() => hide()}>
              <SVG_COMPONENTS.CANCEL_X width={16} height={16} color="#FFFFFF" />
            </TouchableOpacity>
          )}
        </TouchableOpacity>
      </View>
    </View>
  );
};

export const toastConfig = {
  success: (props: ToastConfigParams<any>) => <CustomToast {...props} type="success" />,
  error: (props: ToastConfigParams<any>) => <CustomToast {...props} type="error" />,
  warning: (props: ToastConfigParams<any>) => <CustomToast {...props} type="warning" />,
  info: (props: ToastConfigParams<any>) => <CustomToast {...props} type="info" />,
  loading: (props: ToastConfigParams<any>) => <CustomToast {...props} type="loading" />,
};

const styles = StyleSheet.create({
  toastWrapper: {
    width: '100%',
    maxWidth: Platform.select({
      web: screenWidth > 768 ? 420 : screenWidth - 32,
      default: screenWidth - 32,
    }),
    paddingHorizontal: 16,
    ...Platform.select({
      web: {
        marginHorizontal: screenWidth > 1200 ? 'auto' : 0,
        pointerEvents: 'auto' as any,
      },
    }),
  },
  toast: {
    borderRadius: 12,
    borderWidth: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  toastContent: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    minHeight: 56,
  },
  iconContainer: {
    marginRight: 12,
  },
  message: {
    flex: 1,
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 20,
    color: '#FFFFFF',
  },
  closeButton: {
    marginLeft: 12,
    padding: 4,
  },
});
